<template>
  <div
    class="container flex flex-1 items-center justify-center"
    :class="isFullscreenEnabled ? 'z-[60]' : 'z-10'"
  >
    <div class="relative flex max-h-dvh justify-center pb-6">
      <div
        :class="[
          isPlaying || clickedPlay ? 'opacity-0' : 'opacity-50 hover:opacity-100',
          'absolute left-[21%] top-[15%] z-40 -ml-4 flex aspect-[4/3] w-[65%] cursor-pointer select-none items-center justify-center p-4 transition sm:left-[22%] sm:top-[17%] sm:w-[60%]',
        ]"
        @click="togglePlay"
      >
        <span
          class="select-none font-Turista text-3xl uppercase tracking-wider text-white md:text-4xl"
        >
          {{ t('common.play') }}
        </span>
      </div>
      <img src="/tv_filter.png" alt="TV Filter" class="absolute z-20 opacity-70" />
      <img src="/tv_glass.png" alt="TV Glass" class="absolute z-10 opacity-70 mix-blend-multiply" />
      <img
        src="/tv_blurred.png"
        alt="TV Frame"
        class="pointer-events-none relative z-20 h-auto max-h-dvh"
      />
      <div
        :class="[
          isFullscreenEnabled
            ? 'fixed left-0 top-0 z-[60] h-dvh w-screen bg-black'
            : 'absolute top-[18%] z-0 w-[74%] transition',
          'flex items-center justify-center',
        ]"
      >
        <video
          ref="videoElement"
          :playsinline="!isFullscreenEnabled"
          :muted="isMuted"
          @ended="handleVideoEnded"
        >
          <source src="/ldr-video.mp4" type="video/mp4" />
          {{ t('common.browserDoesNotSupportVideoTag') }}
        </video>
        <X
          v-if="isFullscreenEnabled"
          class="fixed right-8 top-6 z-20 h-8 w-8 stroke-white"
          @click="toggleFullscreen"
        />
      </div>
      <transition name="fade" mode="out-in">
        <button
          v-if="isPlaying"
          src="/fullscreen_tv_button.svg"
          class="fullscreen-tv-button left-[39%] top-[62.5%]"
          @click="toggleFullscreen"
        />
      </transition>

      <RouterLink :to="{ name: 'FanzoneIndex' }" class="absolute bottom-0 z-30 lg:pb-8">
        <div class="group flex items-center" @click="videoPlayerStore.enableFullscreenOnRotate()">
          <img
            src="/arrow/left.svg"
            class="translate-x-2 transition-all group-hover:translate-x-4"
            alt="left"
          />
          <img
            src="/arrow/center.svg"
            alt="center"
            class="transition-all group-hover:-translate-x-2"
          />
          <img
            src="/arrow/right.svg"
            class="-translate-x-2 transition-all group-hover:-translate-x-4"
            alt="right"
          />
        </div>
      </RouterLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { X } from 'lucide-vue-next';
import { useImagePreloader } from '@composables/useImagePreloader';
import { useI18n } from 'vue-i18n';
import { useTelemetryFetcher } from '@api/tracking.api';
import { useFullscreen } from '@vueuse/core';
import { useVideoPlayerStore } from '@stores/videoPlayer.store';

const { t } = useI18n();

const postTelemetry = useTelemetryFetcher();
const videoPlayerStore = useVideoPlayerStore();

const clickedPlay = ref(false);
const videoEnded = ref(false);
const isMuted = ref(false);
const isPlaying = ref(false);
const isFullscreenEnabled = ref(false);
const videoElement = ref<HTMLVideoElement | null>(null);

const { isSupported, toggle } = useFullscreen(videoElement);

// Initialize image preloading
useImagePreloader();

function togglePlay() {
  if (videoElement.value) {
    if (isPlaying.value) {
      videoElement.value.pause();
    } else {
      videoElement.value.play();
      if (!clickedPlay.value) {
        clickedPlay.value = true;
        postTelemetry.execute({
          metric: 'view-splash-video',
          resource: window.location.href,
        });
      }
    }
    isPlaying.value = !isPlaying.value;
  }
}

function handleVideoEnded() {
  if (!videoEnded.value) {
    videoEnded.value = true;
    postTelemetry.execute({
      metric: 'view-splash-video-complete',
      resource: window.location.href,
    });
  }
  // Restart the video
  if (videoElement.value) {
    videoElement.value.currentTime = 0;
    videoElement.value.play();
  }
}

const toggleFullscreen = () => {
  if (isSupported.value) {
    toggle();
  } else {
    isFullscreenEnabled.value = !isFullscreenEnabled.value;
  }
};

postTelemetry.execute({
  metric: 'view-splash',
  resource: window.location.href,
});
</script>

<style>
.tv-button {
  @apply absolute z-30 h-[6%] w-[7%] bg-[url('../tv_button.webp')] bg-contain bg-center bg-no-repeat transition;

  &:active {
    @apply scale-90;
  }
  &:focus {
    @apply outline-none;
  }
}

.fullscreen-tv-button {
  @apply absolute z-30 h-[10%] w-[20%] bg-[url('../fullscreen_tv_button.svg')] bg-contain bg-center bg-no-repeat transition;
  &:active {
    @apply scale-90;
  }
  &:focus {
    @apply outline-none;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.25s,
    transform 0.25s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(0.25rem);
}
</style>
